import { COLORS } from "../shared/constants";
import { IconUser, IconUserPlus, IconZap, IconSettings } from "../shared/icons";
import { canDo, localDateStr } from "../shared/utils";
import { useState, useEffect, useRef } from "react";
import * as api from "../api";
import ScheduleVisitModal from "./ScheduleVisitModal";

export default // ─── Home Screen ─────────────────────────────────────────────
function HomeScreen({ currentUser, onNavigate, patients, onSelectPatient }) {
  const [calConnected, setCalConnected] = useState(false);
  const [events, setEvents] = useState([]);
  const [calLoading, setCalLoading] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [viewDate, setViewDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const calFetched = useRef(false);

  const isToday = localDateStr(viewDate) === localDateStr(new Date());

  const fetchEvents = (date) => {
    setCalLoading(true);
    const dateStr = localDateStr(date);
    api.calendarGetEvents({ date: dateStr, days: 1 })
      .then((res) => {
        if (res && res.events) setEvents(res.events);
        else setEvents([]);
      })
      .catch(() => setEvents([]))
      .finally(() => setCalLoading(false));
  };

  const changeDate = (offset) => {
    const d = new Date(viewDate);
    d.setDate(d.getDate() + offset);
    setViewDate(d);
    fetchEvents(d);
  };

  const tiles = [
    { label: "Load Patient", icon: <IconUser />, color: COLORS.tileBg1, action: "PATIENT_LIST", permission: null },
    { label: "New Patient", icon: <IconUserPlus />, color: COLORS.tileBg2, action: "NEW_PATIENT", permission: "create_edit_patients" },
    { label: "Device Control", icon: <IconZap />, color: COLORS.tileBg3, action: "DEVICE_CONTROL", permission: null },
    { label: "Settings", icon: <IconSettings />, color: COLORS.tileBg4, action: "SETTINGS", permission: "manage_settings" },
  ];

  // Check calendar status and fetch today's events — once only
  useEffect(() => {
    if (calFetched.current) return;
    calFetched.current = true;
    setCalLoading(true);
    api.calendarStatus()
      .then((status) => {
        if (status && status.connected) {
          setCalConnected(true);
          fetchEvents(viewDate);
        }
      })
      .catch((e) => {
        console.log("Calendar check:", e);
      })
      .finally(() => {
        setCalLoading(false);
      });
  }, []);

  // Try to match an event title to an EHR patient
  const matchPatient = (eventSummary) => {
    if (!patients || !eventSummary) return null;
    const lower = eventSummary.toLowerCase();
    return patients.find((p) => {
      const full = `${p.first_name} ${p.last_name}`.toLowerCase();
      const reverse = `${p.last_name} ${p.first_name}`.toLowerCase();
      const lastFirst = `${p.last_name}, ${p.first_name}`.toLowerCase();
      return lower.includes(full) || lower.includes(reverse) || lower.includes(lastFirst);
    });
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return "";
    try {
      const d = new Date(isoStr);
      return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch {
      return isoStr;
    }
  };

  return (
    <div style={{ padding: "48px 24px", maxWidth: "1100px", margin: "0 auto" }}>
      <h2 style={{ color: COLORS.text, fontSize: "22px", fontWeight: "600", marginBottom: "32px", textAlign: "center" }}>
        What would you like to do?
      </h2>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: "16px",
      }}>
        {tiles.map((tile) => {
          const allowed = !tile.permission || canDo(currentUser, tile.permission);
          const active = tile.action && allowed;
          return (
            <button
              key={tile.label}
              onClick={() => active && onNavigate(tile.action)}
              style={{
                background: tile.color,
                color: "#FFFFFF",
                border: "none",
                borderRadius: "14px",
                padding: "36px 20px",
                cursor: active ? "pointer" : tile.action && !allowed ? "not-allowed" : "default",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "14px",
                minHeight: "140px",
                opacity: active ? 1 : tile.action && !allowed ? 0.4 : 0.5,
                transition: "transform 0.15s, box-shadow 0.15s",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
              onMouseEnter={(e) => {
                if (active) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.2)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
              }}
            >
              {tile.icon}
              <span style={{ fontSize: "15px", fontWeight: "600", letterSpacing: "0.2px" }}>{tile.label}</span>
              {!tile.action && (
                <span style={{ fontSize: "11px", opacity: 0.7 }}>Coming soon</span>
              )}
              {tile.action && !allowed && (
                <span style={{ fontSize: "11px", opacity: 0.7 }}>🔒 Restricted</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Today's Schedule from Google Calendar */}
      {calConnected && (
        <div style={{ marginTop: "36px" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.text, marginBottom: "14px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            📅 {isToday ? "Today's Schedule" : "Schedule"}
            <span style={{ fontSize: "12px", fontWeight: "400", color: COLORS.textSecondary, flex: 1 }}>
              {viewDate.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: viewDate.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined })}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <button onClick={() => changeDate(-1)}
                style={{ fontSize: "13px", fontWeight: "600", padding: "6px 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, cursor: "pointer" }}>
                ◀
              </button>
              {!isToday && (
                <button onClick={() => { setViewDate(new Date()); fetchEvents(new Date()); }}
                  style={{ fontSize: "11px", fontWeight: "600", padding: "6px 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.primary, cursor: "pointer" }}>
                  Today
                </button>
              )}
              <button onClick={() => changeDate(1)}
                style={{ fontSize: "13px", fontWeight: "600", padding: "6px 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, cursor: "pointer" }}>
                ▶
              </button>
              <input
                type="date"
                value={localDateStr(viewDate)}
                onChange={(e) => {
                  if (e.target.value) {
                    const d = new Date(e.target.value + "T12:00:00");
                    setViewDate(d);
                    fetchEvents(d);
                  }
                }}
                style={{ fontSize: "12px", padding: "5px 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, color: COLORS.textSecondary, cursor: "pointer" }}
              />
              <button onClick={() => setShowSchedule(true)}
                style={{ fontSize: "12px", fontWeight: "600", padding: "6px 14px", borderRadius: "6px", border: "none", background: COLORS.primary, color: "#fff", cursor: "pointer" }}>
                + Schedule Visit
              </button>
            </div>
          </h3>

          {calLoading ? (
            <div style={{ padding: "20px", textAlign: "center", color: COLORS.textSecondary, fontSize: "13px" }}>
              Loading schedule...
            </div>
          ) : events.length === 0 ? (
            <div style={{
              padding: "20px", textAlign: "center", color: COLORS.textSecondary, fontSize: "13px",
              background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`,
            }}>
              No appointments scheduled for {isToday ? "today" : viewDate.toLocaleDateString(undefined, { month: "long", day: "numeric" })}.
            </div>
          ) : (
            <div style={{
              background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`,
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden",
            }}>
              {events.map((ev, i) => {
                const matched = matchPatient(ev.summary);
                const isPast = ev.end && new Date(ev.end) < new Date();
                return (
                  <div
                    key={ev.id || i}
                    style={{
                      padding: "14px 18px",
                      borderBottom: i < events.length - 1 ? `1px solid ${COLORS.borderLight}` : "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      opacity: isPast ? 0.5 : 1,
                      cursor: matched ? "pointer" : "default",
                      background: matched ? "transparent" : "transparent",
                    }}
                    onClick={() => {
                      if (matched && onSelectPatient) {
                        onSelectPatient(matched);
                      }
                    }}
                    onMouseEnter={(e) => { if (matched) e.currentTarget.style.background = COLORS.primaryLight; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                      {/* Time */}
                      <div style={{ minWidth: "90px" }}>
                        {ev.all_day ? (
                          <span style={{ fontSize: "13px", color: COLORS.textSecondary, fontWeight: "500" }}>All day</span>
                        ) : (
                          <span style={{ fontSize: "13px", fontWeight: "600", color: COLORS.text }}>
                            {formatTime(ev.start)}
                          </span>
                        )}
                      </div>
                      {/* Event details */}
                      <div>
                        <div style={{ fontSize: "14px", fontWeight: "500", color: COLORS.text }}>
                          {ev.summary}
                        </div>
                        {ev.location && (
                          <div style={{ fontSize: "11px", color: COLORS.textSecondary, marginTop: "2px" }}>
                            {ev.location}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      {matched && (
                        <span style={{
                          fontSize: "11px", fontWeight: "600", color: COLORS.primary,
                          background: COLORS.primaryLight, padding: "3px 10px", borderRadius: "12px",
                        }}>
                          #{matched.patient_number} — Open Chart
                        </span>
                      )}
                      {isPast && (
                        <span style={{ fontSize: "11px", color: COLORS.textSecondary }}>Done</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Schedule Visit Modal */}
      {showSchedule && (
        <ScheduleVisitModal
          patients={patients}
          onClose={() => setShowSchedule(false)}
          onCreated={(ev) => {
            // Add to events if it's on the currently viewed date
            const evDate = ev.start ? localDateStr(new Date(ev.start)) : "";
            const viewing = localDateStr(viewDate);
            if (evDate === viewing) {
              setEvents((prev) => [...prev, ev].sort((a, b) => new Date(a.start) - new Date(b.start)));
            }
            setShowSchedule(false);
          }}
        />
      )}
    </div>
  );
}

// ─── Patient List ────────────────────────────────────────────
